import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Smartphone, Globe, Apple, CreditCard, CheckCircle, XCircle } from 'lucide-react';
import { getPlatform, isIOSNative, isNativeApp, getPaymentProvider } from '@/utils/platform';
import { isDespiaNative } from '@/utils/despia';
import { usePlatformPurchase } from '@/hooks/usePlatformPurchase';

interface PlatformInfo {
  platform: string;
  paymentProvider: string;
  isIOSNative: boolean;
  isNativeApp: boolean;
  isDespiaNative: boolean;
  userAgent: string;
  timestamp: string;
  purchaseMethod: 'despia-protocol' | 'stripe';
}

export function PlatformDebugPanel() {
  const [info, setInfo] = useState<PlatformInfo | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const { isInitialized, lastError, isDespia } = usePlatformPurchase();

  const collectPlatformInfo = () => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const despiaNative = isDespiaNative();
    const provider = getPaymentProvider();
    
    return {
      platform: getPlatform(),
      paymentProvider: provider,
      isIOSNative: isIOSNative(),
      isNativeApp: isNativeApp(),
      isDespiaNative: despiaNative,
      userAgent: ua,
      timestamp: new Date().toISOString(),
      purchaseMethod: (despiaNative && provider === 'revenuecat') ? 'despia-protocol' as const : 'stripe' as const,
    };
  };

  useEffect(() => {
    setInfo(collectPlatformInfo());
  }, [refreshCount]);

  const handleRefresh = () => {
    setRefreshCount(c => c + 1);
  };

  if (!info) return null;

  const getPlatformIcon = () => {
    if (info.platform === 'ios') return <Apple className="h-5 w-5" />;
    if (info.platform === 'android') return <Smartphone className="h-5 w-5" />;
    return <Globe className="h-5 w-5" />;
  };

  const getPaymentIcon = () => {
    return <CreditCard className="h-5 w-5" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Platform Detection Debug</h2>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              {getPlatformIcon()}
              Platform
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant={info.platform === 'ios' ? 'default' : 'secondary'} className="text-lg px-3 py-1">
              {info.platform.toUpperCase()}
            </Badge>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              {getPaymentIcon()}
              Payment Provider
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge 
              variant={info.paymentProvider === 'revenuecat' ? 'default' : 'outline'} 
              className="text-lg px-3 py-1"
            >
              {info.paymentProvider === 'revenuecat' ? 'RevenueCat (IAP)' : 'Stripe'}
            </Badge>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Is iOS Native</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant={info.isIOSNative ? 'default' : 'destructive'} className="text-lg px-3 py-1">
              {info.isIOSNative ? 'YES' : 'NO'}
            </Badge>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Is Native App</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant={info.isNativeApp ? 'default' : 'secondary'} className="text-lg px-3 py-1">
              {info.isNativeApp ? 'YES' : 'NO'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Despia Purchase Status */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-base">Purchase Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="text-sm text-muted-foreground">isDespiaNative</span>
              <Badge variant={info.isDespiaNative ? 'default' : 'outline'}>
                {info.isDespiaNative ? 'YES' : 'NO'}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="text-sm text-muted-foreground">Purchase Method</span>
              <Badge variant={info.purchaseMethod === 'despia-protocol' ? 'default' : 'secondary'}>
                {info.purchaseMethod === 'despia-protocol' ? 'Despia Protocol' : 'Stripe'}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="text-sm text-muted-foreground">Hook Initialized</span>
              <Badge variant={isInitialized ? 'default' : 'destructive'}>
                {isInitialized ? 'YES' : 'NO'}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="text-sm text-muted-foreground">Contains "despia" in UA</span>
              <Badge variant={info.userAgent.toLowerCase().includes('despia') ? 'default' : 'outline'}>
                {info.userAgent.toLowerCase().includes('despia') ? 'YES' : 'NO'}
              </Badge>
            </div>
          </div>
          {lastError && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
              <p className="text-sm text-destructive">Last Error: {lastError}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product IDs */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-base">Product IDs (iOS)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <code className="block p-2 bg-muted/50 rounded text-xs font-mono">
              Monthly: evolve_premium_monthly
            </code>
            <code className="block p-2 bg-muted/50 rounded text-xs font-mono">
              Yearly: evolve_premium_yearly
            </code>
          </div>
        </CardContent>
      </Card>

      {/* User Agent */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-base">User Agent</CardTitle>
        </CardHeader>
        <CardContent>
          <code className="block p-3 bg-muted/50 rounded-lg text-xs break-all font-mono">
            {info.userAgent}
          </code>
          <p className="text-xs text-muted-foreground mt-2">
            Last refreshed: {info.timestamp}
          </p>
        </CardContent>
      </Card>

      {/* Expected Behavior */}
      <Card className={`border-2 ${info.isDespiaNative && info.purchaseMethod === 'despia-protocol' ? 'border-green-500/50 bg-green-500/5' : 'border-yellow-500/50 bg-yellow-500/5'}`}>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            {info.isDespiaNative && info.purchaseMethod === 'despia-protocol' 
              ? <><CheckCircle className="h-5 w-5 text-green-500" /> Correct iOS Configuration</> 
              : <><XCircle className="h-5 w-5 text-yellow-500" /> Configuration Check</>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {info.isDespiaNative && info.purchaseMethod === 'despia-protocol' ? (
            <div className="space-y-2">
              <p className="text-sm text-green-400">
                ✅ Despia native detected - will use despia("revenuecat://purchase?...") protocol
              </p>
              <p className="text-xs text-muted-foreground">
                Purchases will trigger native iOS App Store payment sheet via Despia SDK.
              </p>
            </div>
          ) : info.platform === 'web' ? (
            <div className="space-y-2">
              <p className="text-sm text-yellow-400">
                Platform detected as WEB - will use Stripe for purchases.
              </p>
              <p className="text-xs text-muted-foreground">
                If you're viewing this in the iOS app, check if "despia" is in the user agent.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Platform: {info.platform}, Payment: {info.paymentProvider}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
